import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ChapterNumbersChapterNumbersItem,
  Company,
  ErrorInfo,
  PrivilegesItem,
  TeamTreesCompaniesItem,
  TeamTreesCompaniesItemGroupsItem,
  UserDetail,
} from '../../../../api/model';
import { usePostUsersSave } from '../../../../api/users/users';
import { useDefaultOptions } from '../../../../hooks/api';
import yup from '../../../../utils/custom-yup';
import MultiSelect from '../../../atoms/MultiSelect';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import SlideToggle from '../../../modules/SlideToggle';
import SpinnerModal from '../../../modules/SpinnerModal';
import { DisplayUserModal } from './DisplayUserModal';
import { UserPasswordModal } from './UserPasswordModal';

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC|ユーザー情報')}</title>
    </Helmet>
  );
}

type Props = {
  user: UserDetail;
  companies: Company[];
  priviledges: PrivilegesItem[];
  chapterNumbers: ChapterNumbersChapterNumbersItem[];
  teamCompanies: TeamTreesCompaniesItem[];
  transitionSource: string;
};
function UserForm({ user, companies, priviledges, chapterNumbers, teamCompanies, transitionSource }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);
  const [displayUserModal, setDisplayUserModal] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [isError, setIsError] = useState(false);
  const navigate = useNavigate();
  const [openUserPassword, setOpenUserPassword] = useState(false);

  // グループ
  const findGroups = (companyId?: number) => {
    const company = teamCompanies.find((c) => c.company_id === companyId);
    return company?.groups;
  };

  const [groups, setGroups] = useState<TeamTreesCompaniesItemGroupsItem[]>();
  const handleChangeCompany = (companyId: string) => {
    setGroups(findGroups(Number(companyId)));
  };

  const teamUserMessage = t('グループ情報を選択してください');
  const userPrivilegesMessage = t('利用者権限を選択してください');
  const schema = yup.object().shape({
    user_id_text: yup.string().required(),
    user_name_ja: yup.string().required(),
    user_name_en: yup.string().required(),
    company_id: yup.string().required(),
    team_users: yup.array().min(1, teamUserMessage),
    user_privileges: yup.array().min(1, userPrivilegesMessage),
    email_address: yup.string().required(),
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<UserDetail>({
    defaultValues: {
      user_id_text: user.user_id_text,
      user_id: user.user_id,
      user_name_ja: user.user_name_ja,
      user_name_en: user.user_name_en,
      company_id: user.company_id,
      job_number: user.job_number,
      email_address: user.email_address,
      team_users: user.team_users,
      memo: user.memo,
      unlock_authority: user.unlock_authority,
      user_chapter_numbers: user.user_chapter_numbers,
      group_task_user: user.group_task_user,
      user_privileges: user.user_privileges,
      user_mail_notifications: user?.user_mail_notifications,
      password: user.password,
    },
    resolver: yupResolver(schema),
  });

  const groupTaskUsers = watch('group_task_user');
  const { fields, append, remove } = useFieldArray({ name: 'group_task_user', control });
  const { mutateAsync, isSuccess, error, isLoading } = usePostUsersSave<AxiosError<ErrorInfo>, unknown>(options);
  const onSubmit = useCallback(
    () =>
      handleSubmit((data: UserDetail) => {
        const postUserSave = async () => {
          await mutateAsync({
            data,
          });
        };

        void postUserSave();
      })(),
    [mutateAsync, handleSubmit]
  );
  const Editable = user?.editable ?? false;
  const [privilege1, setPrivilege1] = useState<number | null>(
    (user.user_privileges && user.user_privileges[0]) || null
  );
  const [privilege2, setPrivilege2] = useState<number | null>(
    (user.user_privileges && user.user_privileges[1]) || null
  );

  // 新規フラグ
  const newFlg = Number(user.user_id) === 0;

  const setPrivileges1 = (value1?: number) => {
    const values: number[] = [];
    if (value1) {
      values.push(value1);
    }
    if (privilege2) {
      values.push(privilege2);
    }
    setValue('user_privileges', values);
  };

  const setPrivileges2 = (value2?: number) => {
    const values: number[] = [];
    if (privilege1) {
      values.push(privilege1);
    }
    if (value2) {
      values.push(value2);
    }
    setValue('user_privileges', values);
  };
  const handleChangePrivilege1 = (value: string) => {
    setPrivilege1(Number(value));
    setPrivileges1(Number(value));
  };

  const handleChangePrivilege2 = (value: string) => {
    setPrivilege2(Number(value));
    setPrivileges2(Number(value));
  };

  const [grpOpen, setGrpOpen] = useState(false);
  const handleGrpOpen = () => {
    setGrpOpen(!grpOpen);
  };

  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setOpen(!open);
  };
  const handleSubmitButton = () => {
    void onSubmit();
    setIsConfirm(false);
  };
  const handleSubmitCancel = () => {
    setIsConfirm(false);
  };

  useEffect(() => {
    const company = teamCompanies.find((c) => c.company_id === user.company_id);
    setGroups(company?.groups);
  }, [user.company_id, teamCompanies]);

  useEffect(() => {
    if (isSuccess) {
      if (transitionSource === 'home') {
        navigate(`/${language}/home`);
      } else {
        navigate('..');
      }
    }
  }, [navigate, isSuccess, transitionSource, language]);

  useEffect(() => {
    setIsError(error != null);
  }, [error]);

  return (
    <div id="main">
      <Title />
      <section>
        <h3>
          <span>{t('ユーザー情報')}</span>
        </h3>
        <form>
          <div className="wfr scondition">
            <div className="sepfr3 spm">
              <div className="labip1">
                <div>
                  <label htmlFor="user_id_text">{t('ユーザーID')}</label>
                  <div>
                    <input
                      type="text"
                      id="user_id_text"
                      className={errors.user_id_text?.message ? 'error' : ''}
                      {...register('user_id_text')}
                    />
                    {errors.user_id_text?.message && (
                      <p className="errormessage show">{errors.user_id_text?.message.toString()}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor="user_name_ja">{t('ユーザー名（日）')}</label>
                  <div>
                    <input
                      type="text"
                      id="user_name_ja"
                      className={errors.user_name_ja?.message ? 'error' : ''}
                      {...register('user_name_ja')}
                    />
                    {errors.user_name_ja?.message && (
                      <p className="errormessage show">{errors.user_name_ja?.message}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor="user_name_en">{t('ユーザー名（英）')}</label>
                  <div>
                    <input
                      type="text"
                      id="user_name_en"
                      className={errors.user_name_en?.message ? 'error' : ''}
                      {...register('user_name_en')}
                    />
                    {errors.user_name_en?.message && (
                      <p className="errormessage show">{errors.user_name_en?.message}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor="company_name">{t('会社名')}</label>
                  <span className="dropdown">
                    <select
                      className={errors.company_id?.message ? 'error' : ''}
                      {...register('company_id')}
                      onChange={(e) => handleChangeCompany(e.target.value)}
                      disabled={!user.system_privilage}
                    >
                      <option value="">{t('選択してください')}</option>
                      {companies.map((company) => (
                        <option key={company.company_id} value={company.company_id}>
                          {company.company_name}
                        </option>
                      ))}
                    </select>
                    {errors.company_id?.message && <p className="errormessage show">{errors.company_id?.message}</p>}
                  </span>
                </div>
                <div>
                  <label htmlFor="job_number">{t('職番')}</label>
                  <input type="text" id="job_number" {...register('job_number')} />
                </div>
              </div>
              <div className="labip1 groupelab">
                <div className="groupefr">
                  <p className="ttl">{t('グループ情報')}</p>
                  <div>
                    <div className={`groupefr-in ${errors.team_users?.message ? 'error' : ''}`}>
                      {groups?.map((group) => (
                        <div key={group.group_id}>
                          <p>{group.group_name}</p>
                          {group.teams?.map((team) => (
                            <span className="ck-left" key={`team-${String(group.group_id)}-${String(team.team_id)}`}>
                              <input
                                type="checkbox"
                                id={`team-${team.team_id || ''}`}
                                value={team.team_id}
                                defaultChecked={user.team_users?.includes(team.team_id || 0)}
                                {...register('team_users')}
                              />
                              <label htmlFor={`team-${team.team_id || ''}`}>{team.team_name}</label>
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                    {errors.team_users?.message && <p className="errormessage show">{errors.team_users?.message}</p>}
                  </div>
                </div>
              </div>
              <div className="labip1">
                <div>
                  <label htmlFor="priv1">{t('利用者権限1')}</label>
                  <div>
                    <span className="dropdown">
                      <select
                        name="item"
                        id="priv1"
                        value={String(privilege1)}
                        onChange={(e) => handleChangePrivilege1(e.target.value)}
                        disabled={!user.system_privilage}
                      >
                        <option value="">{t('選択してください')}</option>
                        {priviledges.map((previlege) => (
                          <option key={`prev1-${previlege.privilege_id || ''}`} value={String(previlege.privilege_id)}>
                            {previlege.privilege_name}
                          </option>
                        ))}
                      </select>
                    </span>
                    {errors.user_privileges?.message && (
                      <p className="errormessage show">{errors.user_privileges?.message}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor="priv2">{t('利用者権限2')}</label>
                  <span className="dropdown">
                    <select
                      name="item"
                      id="priv2"
                      value={String(privilege2)}
                      onChange={(e) => handleChangePrivilege2(e.target.value)}
                      disabled={!user.system_privilage}
                    >
                      <option value="">{t('選択してください')}</option>
                      {priviledges.map((previlege) => (
                        <option key={`prev2-${previlege.privilege_id || ''}`} value={String(previlege.privilege_id)}>
                          {previlege.privilege_name}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>
                <div>
                  <label htmlFor="email_address">{t('メールアドレス')}</label>
                  <div>
                    <input
                      type="text"
                      id="email_address"
                      className={errors.email_address?.message ? 'error' : ''}
                      {...register('email_address')}
                    />
                    {errors.email_address?.message && (
                      <p className="errormessage show">{errors.email_address?.message}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor="user_chapter_numbers">{t('原稿編集（章）')}</label>
                  <MultiSelect
                    id="user_chapter_numbers"
                    name="user_chapter_numbers"
                    options={chapterNumbers}
                    control={control}
                    isDisabled={false}
                    getValue={(x) => x.chapter_number_id}
                    getLabel={(x) => x.chapter_name}
                  />
                </div>
                <div style={{ justifyContent: 'flex-end' }}>
                  <span>
                    <input type="checkbox" id="unlock_authority" {...register('unlock_authority')} disabled={!user.system_privilage}/>
                    <label htmlFor="unlock_authority" style={{ paddingBottom: '0px', paddingRight: '10px' }}>
                      &nbsp;
                    </label>
                    <label className="ml10" style={{ fontWeight: 'normal', color: !user.system_privilage ? '#979797' : '' }}>
                      {t('編集解除権限(Prod.管理者より設定)')}
                    </label>
                  </span>
                </div>
                {newFlg ? (
                  <div>
                    <label htmlFor="password">{t('パスワード')}</label>
                    <div>
                      <input
                        type="password"
                        id="password"
                        className={errors.password?.message ? 'error' : ''}
                        {...register('password')}
                      />
                      {errors.password?.message && <p className="errormessage show">{errors.password?.message}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="master-navi" style={{ justifyContent: 'flex-end' }}>
                    <ul>
                      <li onClick={() => setOpenUserPassword(true)}>
                        <i className="fa-solid fa-user-lock" />
                        {t('パスワード変更')}
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div className="formarea1 mt20 mb40">
              <label htmlFor="memo">{t('内容')}</label>
              <textarea id="memo" rows={10} cols={33} {...register('memo')} />
            </div>
            <div>
              <h4 className="mdh2-4 trig" onClick={handleGrpOpen} style={{ display: 'inline-block' }}>
                <span>
                  {t('Display(Group)タブにタスクを表示するユーザー')}
                  <i className={`fa-solid ${grpOpen ? 'fa-chevron-down' : 'fa-chevron-up'}`} />
                </span>
              </h4>
              <SlideToggle open={grpOpen}>
                <div className="master-navi groupeu">
                  <ul>
                    <li className="" onClick={() => setDisplayUserModal(true)}>
                      <i className="fa-solid fa-circle-plus" />
                      {t('ユーザー追加')}
                      <DisplayUserModal
                        open={displayUserModal}
                        baseUsers={groupTaskUsers || []}
                        userId={user.user_id}
                        onClose={() => setDisplayUserModal(false)}
                        add={append}
                      />
                    </li>
                  </ul>
                </div>
                <table className="table41 mb30">
                  <tbody>
                    <tr>
                      <th> </th>
                      <th>{t('ユーザー')}</th>
                      <th>{t('ユーザーID')}</th>
                      <th>{t('メールアドレス')}</th>
                      <th>{t('会社名')}</th>
                      <th>{t('グループ・チーム')}</th>
                      <th>{t('職番')}</th>
                      <th>{t('担当章')}</th>
                    </tr>
                    {fields.map((field, i) => (
                      <tr key={`group-task-user-${field.id}`}>
                        <th>
                          <span className="ck-box" onClick={() => remove(i)}>
                            <i className="fa-solid fa-circle-xmark" />
                          </span>
                        </th>
                        <th>{field.user_name}</th>
                        <th>{field.user_id_text}</th>
                        <th>{field.email_address}</th>
                        <th>{field.company_name}</th>
                        <th>{field.group_name}</th>
                        <th>{field.job_number}</th>
                        <th>{field.user_chapter}</th>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SlideToggle>
            </div>
            <div>
              <h4 className="mdh2-4 trig" onClick={handleOpen} style={{ display: 'inline-block' }}>
                <span>
                  {t('ユーザーメール通知')}
                  <i className={`fa-solid ${open ? 'fa-chevron-down' : 'fa-chevron-up'}`} />
                </span>
              </h4>
              <SlideToggle open={open}>
                <table className="table40 mb30">
                  <tbody>
                    <tr>
                      <th style={{ whiteSpace: 'pre' }}>{t('通知する')}</th>
                      <th>{t('メール通知項目')}</th>
                      <th>{t('通知タイミング')}</th>
                      <th>{t('宛先')}</th>
                      <th>{t('通知内容')}</th>
                    </tr>
                    {user.user_mail_notifications?.map((userMailNotification, i) => (
                      <tr key={`user-${String(userMailNotification.user_mail_notification_id)}-${String(i)}`}>
                        <td>
                          {!newFlg ? (
                            <span className="ck-box">
                              <input
                                type="checkbox"
                                id={String(userMailNotification.user_mail_notification_id)}
                                {...register(`user_mail_notifications.${i}.notice`)}
                                onChange={(e) =>
                                  setValue(`user_mail_notifications.${i}.notice`, e.target.checked ? 1 : 0)
                                }
                              />{' '}
                              <label htmlFor={String(userMailNotification.user_mail_notification_id)}>&nbsp;</label>
                            </span>
                          ) : (
                            <span className="ck-box">
                              <input
                                type="checkbox"
                                id={`user-${String(i)}`}
                                {...register(`user_mail_notifications.${i}.notice`)}
                                onChange={(e) =>
                                  setValue(`user_mail_notifications.${i}.notice`, e.target.checked ? 1 : 0)
                                }
                              />{' '}
                              <label htmlFor={`user-${String(i)}`}>&nbsp;</label>
                            </span>
                          )}
                        </td>
                        <td>{userMailNotification.milestone}</td>
                        <td>{userMailNotification.notification_timing_expression}</td>
                        <td>{userMailNotification.destination}</td>
                        <td>{userMailNotification.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SlideToggle>
            </div>
          </div>
          <div className="controlfr btnfr btnfr1 mb0">
            <div>
              <span className="mr10">
                <input type="button" value={t('クリア').toString()} className="btn1 btnbg-g" onClick={() => reset()} />
              </span>
              <span className="btn2 btnbg-g mr10">
                <input
                  type="button"
                  value={t('キャンセル').toString()}
                  className="btn1 btnbg-g"
                  onClick={() => {
                    if (transitionSource === 'home') {
                      navigate(`/${language}/home`);
                    } else {
                      navigate('..');
                    }
                  }}
                />
              </span>
              <span>
                <input
                  type="button"
                  value={t('登録/更新').toString()}
                  className="btn1 lsp03"
                  onClick={() => setIsConfirm(true)}
                  disabled={!Editable}
                />
              </span>
            </div>
          </div>
          {isLoading && <SpinnerModal />}
        </form>
      </section>
      {isConfirm && (
        <ConfirmModal onClickOk={() => handleSubmitButton()} onClickCancel={() => handleSubmitCancel()}>
          <div className="poptxt">
            <p>{t('更新します。よろしいですか？')}</p>
          </div>
        </ConfirmModal>
      )}
      {isError && (
        <ErrorModal
          onClickOk={() => {
            setIsError(false);
          }}
          error={error}
        />
      )}
      {/* パスワード変更ダイアログ */}
      {openUserPassword && (
        <UserPasswordModal UserId={user.user_id} open={openUserPassword} onClose={() => setOpenUserPassword(false)} />
      )}
    </div>
  );
}

export { UserForm };
